import React, { useCallback, useEffect, useState } from 'react';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAlert } from '../contexts/AlertContext';
import { useWorkSites } from '../hooks/useWorkSites';
import { AppCard, linkedAppsApi, LinkTarget } from '../services/linkedAppsApi';
import type { WorkSiteDef } from '../types/workSites';
import { openWorkSite, WORK_SITES } from '../utils/workSites';

/**
 * 업무 사이트 › 전체 현황 (/work-sites, GRK_admin#115) — 프로젝트 · 채용 · 투자 현황 카드를 한 페이지에.
 * 숫자 · 버튼을 누르면 그 사이트의 해당 화면이 새 탭으로, 누른 사람 계정으로 열린다(다시 로그인하지 않음).
 * 볼 수 있는 사람 · 사이트는 백엔드가 정한다(GET /api/sso/sites). 연결 설정 안 된 사이트는 카드가 없다.
 */
const WorkSitesPage: React.FC = () => {
  const ws = useWorkSites();
  const allow = ws.loading ? null : ws.allowed && ws.sites.length > 0;
  return (
    <ProtectedRoute allow={allow}>
      <Overview sites={WORK_SITES.filter((s) => ws.sites.includes(s.target))} />
    </ProtectedRoute>
  );
};

const Overview: React.FC<{ sites: WorkSiteDef[] }> = ({ sites }) => {
  const { showToast } = useAlert();
  const [cards, setCards] = useState<AppCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCards(await linkedAppsApi.summary());
    } catch (e) {
      showToast(`현황을 불러오지 못했습니다: ${(e as Error).message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const go = async (target: LinkTarget, next?: string) => {
    setMoving(`${target}:${next ?? ''}`);
    try {
      await openWorkSite(target, next);
    } catch (e) {
      showToast(`이동하지 못했습니다: ${(e as Error).message}`, 'error');
    } finally {
      setMoving(null);
    }
  };

  const updatedAt = cards
    .map((c) => (c.state === 'ok' ? c.summary.updatedAt : ''))
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-slate-800">경영현황</h1>
        <span className="text-sm text-slate-400">누르면 새 탭 · 내 계정으로 바로 로그인</span>
        <span className="flex-1" />
        {updatedAt && <span className="text-xs text-slate-400">기준 {formatTime(updatedAt)}</span>}
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </header>

      <div className="grid gap-4 items-start grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
        {sites.map((site) => (
          <SiteCard
            key={site.target}
            site={site}
            card={cards.find((c) => c.target === site.target)}
            loading={loading}
            moving={moving}
            go={go}
          />
        ))}
      </div>
    </div>
  );
};

const SiteCard: React.FC<{
  site: WorkSiteDef;
  card: AppCard | undefined;
  loading: boolean;
  moving: string | null;
  go: (target: LinkTarget, next?: string) => void;
}> = ({ site, card, loading, moving, go }) => {
  const busy = moving !== null;
  const ok = card?.state === 'ok' ? card : null;
  const failed = !loading && !ok;
  const open = (next?: string) => go(site.target, next);

  return (
    <section className="bg-white rounded-lg shadow border border-slate-200 p-5 flex flex-col gap-4 min-w-0">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-bold text-slate-800">{site.label}</h2>
        {ok && <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">연결됨</span>}
        {failed && <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">응답 없음</span>}
        <span className="flex-1" />
        <button
          type="button"
          disabled={busy}
          onClick={() => open()}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
        >
          {moving === `${site.target}:` ? '여는 중…' : '열기 ↗'}
        </button>
      </div>

      {loading && !card ? (
        <div className="grid grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-50 animate-pulse" />
          ))}
        </div>
      ) : ok ? (
        ok.summary.items.length === 0 ? (
          <p className="text-sm text-slate-500">표시할 항목이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {ok.summary.items.map((item) => (
              <button
                key={item.key}
                type="button"
                disabled={busy}
                onClick={() => open(item.link)}
                title={`${item.label} 화면 열기`}
                className="text-left px-3 py-2.5 rounded-lg bg-slate-50 hover:bg-indigo-50 disabled:opacity-60 transition-colors min-w-0"
              >
                <div className="text-xs text-slate-500 truncate">{item.label}</div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums">{formatValue(item.value)}</div>
              </button>
            ))}
          </div>
        )
      ) : (
        <p className="text-sm text-slate-500">요약을 받지 못했습니다. 열기 · 바로 가기는 됩니다.</p>
      )}

      {site.shortcuts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {site.shortcuts.map((sc) => (
            <button
              key={sc.path}
              type="button"
              disabled={busy}
              onClick={() => open(sc.path)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-600 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-60"
            >
              {moving === `${site.target}:${sc.path}` ? '여는 중…' : `${sc.label} ↗`}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

function formatValue(v: number | string): string {
  return typeof v === 'number' ? v.toLocaleString('ko-KR') : v;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default WorkSitesPage;
